import { Router } from 'express';
import { PRDService } from '../services/prd-service.js';
import { ApiError, ApiErrorCode } from '../types.js';
import { logger } from '../../utils/logger.js';

export const prdRouter = Router();
const prdService = new PRDService();

// 获取所有 PRD
prdRouter.get('/', async (req, res, next) => {
  try {
    const prds = await prdService.list();
    res.success(prds);
  } catch (error) {
    next(error);
  }
});

// 获取单个 PRD
prdRouter.get('/:id', async (req, res, next) => {
  try {
    const prd = await prdService.get(req.params.id);
    if (!prd) {
      throw new ApiError(404, 'PRD not found');
    }
    res.success(prd);
  } catch (error) {
    next(error);
  }
});

// 创建 PRD
prdRouter.post('/', async (req, res, next) => {
  try {
    const { title, status, version, author, tags, linkedTasks, sections } = req.body;
    
    if (!title || !author) {
      throw new ApiError(400, 'Title and author are required');
    }

    const prd = await prdService.create({
      title,
      status: status || 'draft',
      version: version || '1.0.0',
      author,
      tags: tags || [],
      linkedTasks: linkedTasks || [],
      sections: sections || {}
    });
    
    res.created(prd);
  } catch (error) {
    next(error);
  }
});

// 更新 PRD
prdRouter.put('/:id', async (req, res, next) => {
  try {
    const updates = req.body;
    const prd = await prdService.update(req.params.id, updates);
    res.success(prd);
  } catch (error) {
    if (error instanceof Error && error.message === 'PRD not found') {
      next(new ApiError(404, error.message));
    } else {
      next(error);
    }
  }
});

// 删除 PRD
prdRouter.delete('/:id', async (req, res, next) => {
  try {
    await prdService.delete(req.params.id);
    res.deleted();
  } catch (error) {
    if (error instanceof Error && error.message === 'PRD not found') {
      next(new ApiError(404, error.message));
    } else {
      next(error);
    }
  }
});

// 更新关联的任务
prdRouter.put('/:id/tasks', async (req, res, next) => {
  try {
    const { taskIds } = req.body;
    if (!Array.isArray(taskIds)) {
      throw new ApiError(400, 'taskIds must be an array');
    }
    
    await prdService.updateLinkedTasks(req.params.id, taskIds);
    res.success({ message: 'Linked tasks updated' });
  } catch (error) {
    if (error instanceof Error && error.message === 'PRD not found') {
      next(new ApiError(404, error.message));
    } else {
      next(error);
    }
  }
});

// 导出为 Markdown
prdRouter.get('/:id/export', async (req, res, next) => {
  try {
    const markdown = await prdService.exportToMarkdown(req.params.id);
    res.setHeader('Content-Type', 'text/markdown');
    res.setHeader('Content-Disposition', `attachment; filename="prd-${req.params.id}.md"`);
    res.send(markdown);
  } catch (error) {
    if (error instanceof Error && error.message === 'PRD not found') {
      next(new ApiError(404, error.message));
    } else {
      next(error);
    }
  }
});

// 获取 PRD 统计信息
prdRouter.get('/:id/stats', async (req, res, next) => {
  try {
    const { id } = req.params;
    const stats = await prdService.getStats(id);
    
    if (!stats) {
      throw new ApiError(
        ApiErrorCode.NOT_FOUND,
        `PRD with id ${id} not found or has no tasks`,
        404
      );
    }
    
    res.success(stats);
  } catch (error) {
    next(error);
  }
});

// 获取 PRD 进度报告
prdRouter.get('/:id/report', async (req, res, next) => {
  try {
    const { id } = req.params;
    const report = await prdService.generateReport(id);
    
    res.success({ report });
  } catch (error) {
    next(error);
  }
});

// 更新 PRD 进度
prdRouter.post('/:id/update-progress', async (req, res, next) => {
  try {
    const { id } = req.params;
    
    await prdService.updateProgress(id);
    const prd = await prdService.get(id);
    
    logger.info(`PRD progress updated: ${id}`);
    
    res.success(prd);
  } catch (error) {
    next(error);
  }
});

// 使用 AI 生成 PRD
prdRouter.post('/generate', async (req, res, next) => {
  try {
    const { idea, template, autoCreateTasks } = req.body;
    
    if (!idea) {
      throw new ApiError(400, 'Idea is required');
    }
    
    // 动态导入 PRD 生成器
    const { PRDGenerator } = await import('../../core/prd-generator.js');
    const generator = new PRDGenerator();
    
    // 生成 PRD
    const prdContent = await generator.generateFromIdea(idea, {
      template: template || 'default'
    });
    
    // 创建 PRD
    const prd = await prdService.create({
      title: prdContent.title || idea.substring(0, 50),
      status: 'draft',
      version: '1.0.0',
      author: 'AI Generator',
      tags: prdContent.tags || [],
      linkedTasks: [],
      sections: prdContent.sections || {},
      content: prdContent.content
    });
    
    // 如果需要自动创建任务
    if (autoCreateTasks !== false && prdContent.tasks && prdContent.tasks.length > 0) {
      const { TaskStore } = await import('../../core/task-store.js');
      const taskStore = new TaskStore();
      
      for (const taskData of prdContent.tasks) {
        const task = await taskStore.createTask({
          content: taskData.name || taskData.content,
          description: taskData.description,
          status: 'todo',
          priority: taskData.priority || 'medium',
          tags: taskData.tags || [],
          metadata: {
            prdId: prd.id,
            source: 'prd-generator'
          }
        });
        
        // 链接任务到 PRD
        await prdService.linkTask(prd.id, task.id);
      }
    }
    
    logger.info(`PRD generated from idea: ${prd.id}`);
    res.success(prd);
  } catch (error) {
    logger.error('Failed to generate PRD:', error);
    next(error);
  }
});

// 获取 PRD 模板列表
prdRouter.get('/templates', async (req, res, next) => {
  try {
    // 返回可用的模板
    const templates = [
      { id: 'default', name: '默认模板', description: '标准的产品需求文档模板' },
      { id: 'simple', name: '简化模板', description: '精简版的 PRD 模板' },
      { id: 'detailed', name: '详细模板', description: '包含更多细节的完整模板' },
      { id: 'agile', name: '敏捷模板', description: '适用于敏捷开发的用户故事模板' }
    ];
    
    res.success(templates);
  } catch (error) {
    next(error);
  }
});